import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Divider, Empty, Form, Input, Modal, Select, Space, Switch, Table, Tag, Typography, message } from 'antd';
import { useState } from 'react';
import { FaBuildingColumns, FaMobile, FaPlus } from 'react-icons/fa6';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { PaymentAccount } from '../../types/payment-account.types';

const { Title, Text } = Typography;
const { Option } = Select;

type AccountFormValues = {
	type: 'mobile_money' | 'bank';
	account_number: string;
	bank_name?: string;
	branch_name?: string;
};

const PaymentAccounts = () => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [accountForm] = Form.useForm<AccountFormValues>();
	const [accountType, setAccountType] = useState<'mobile_money' | 'bank'>('mobile_money');
	const queryClient = useQueryClient();

	// Fetch payment accounts
	const { data, isLoading } = useQuery<{ results: PaymentAccount[] }>({
		queryKey: ['paymentAccounts'],
		queryFn: async () => {
			const { data } = await axios.get('/retailers/payment-accounts', { withCredentials: true });
			return data;
		},
	});

	// Add payment account mutation
	const { mutate: addAccount, isPending: isAddingAccount } = useMutation({
		mutationFn: async (values: AccountFormValues) => {
			const { data } = await axios.post('/retailers/payment-accounts', values, { withCredentials: true });
			return data;
		},
		onSuccess: () => {
			message.success('Payment account added successfully');
			queryClient.invalidateQueries({ queryKey: ['paymentAccounts'] });
			setIsModalOpen(false);
			accountForm.resetFields();
		},
		onError: (error) => {
			console.error('Error adding payment account:', error);
			message.error('Failed to add payment account');
		},
	});

	// Deactivate payment account mutation
	const { mutate: deactivateAccount, isPending: isDeactivating } = useMutation({
		mutationFn: async (id: number) => {
			const { data } = await axios.post(`/retailers/payment-accounts/${id}/deactivate`, {}, { withCredentials: true });
			return data;
		},
		onSuccess: () => {
			message.success('Payment account status updated');
			queryClient.invalidateQueries({ queryKey: ['paymentAccounts'] });
		},
		onError: (error) => {
			console.error('Error updating payment account:', error);
			message.error('Failed to update payment account status');
		},
	});

	const handleAddAccount = (values: AccountFormValues) => {
		addAccount(values);
	};

	const handleStatusChange = (checked: boolean, record: PaymentAccount) => {
		if (!checked) {
			deactivateAccount(record.id);
		}
	};

	const columns = [
		{
			title: 'Type',
			dataIndex: 'type',
			key: 'type',
			render: (type: string) => (
				<Tag
					color={type === 'mobile_money' ? 'purple' : 'blue'}
					icon={type === 'mobile_money' ? <FaMobile /> : <FaBuildingColumns />}
					className="flex w-fit items-center gap-2"
				>
					{type === 'mobile_money' ? 'Mobile Money' : 'Bank Account'}
				</Tag>
			),
		},
		{
			title: 'Account Number',
			dataIndex: 'account_number',
			key: 'account_number',
		},
		{
			title: 'Bank',
			dataIndex: 'bank_name',
			key: 'bank_name',
			render: (bank_name: string) => bank_name || '-',
		},
		{
			title: 'Branch',
			dataIndex: 'branch_name',
			key: 'branch_name',
			render: (branch_name: string) => branch_name || '-',
		},
		{
			title: 'Status',
			dataIndex: 'is_active',
			key: 'is_active',
			render: (is_active: boolean, record: PaymentAccount) => (
				<Space>
					<Switch
						checked={is_active}
						onChange={(checked) => handleStatusChange(checked, record)}
						disabled={isDeactivating}
					/>
					<span>{is_active ? 'Active' : 'Inactive'}</span>
				</Space>
			),
		},
	];

	return (
		<AppLayout
			title="Payment Accounts"
			subtitle="Manage your payment accounts here."
		>
			<Card className="mb-6 overflow-hidden">
				<div className="flex flex-wrap items-center justify-between gap-4">
					<div>
						<Title
							level={4}
							className="mb-1"
						>
							Payment Accounts
						</Title>
						<Text type="secondary">Manage accounts for receiving payments</Text>
					</div>
					<Button
						type="primary"
						icon={<FaPlus />}
						onClick={() => setIsModalOpen(true)}
					>
						Add Payment Account
					</Button>
				</div>
			</Card>

			<Card>
				{data?.results && data.results.length > 0 ? (
					<Table
						dataSource={data.results}
						columns={columns}
						rowKey="id"
						loading={isLoading}
						pagination={false}
					/>
				) : (
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description={
							<div className="text-center">
								<p className="mb-4 text-gray-500">No payment accounts found</p>
								<Button
									type="primary"
									icon={<FaPlus />}
									onClick={() => setIsModalOpen(true)}
								>
									Add Payment Account
								</Button>
							</div>
						}
					/>
				)}
			</Card>

			{/* Add Payment Account Modal */}
			<Modal
				title="Add Payment Account"
				open={isModalOpen}
				onCancel={() => {
					setIsModalOpen(false);
					accountForm.resetFields();
				}}
				footer={null}
				destroyOnClose
			>
				<Form
					form={accountForm}
					layout="vertical"
					onFinish={handleAddAccount}
					initialValues={{ type: 'mobile_money' }}
					requiredMark={false}
				>
					<Form.Item
						name="type"
						label="Account Type"
						rules={[{ required: true, message: 'Please select account type' }]}
					>
						<Select onChange={(value) => setAccountType(value as 'mobile_money' | 'bank')}>
							<Option value="mobile_money">Mobile Money</Option>
							<Option value="bank">Bank Account</Option>
						</Select>
					</Form.Item>

					<Form.Item
						name="account_number"
						label="Account Number"
						rules={[{ required: true, message: 'Please enter account number' }]}
					>
						<Input placeholder="Enter account number" />
					</Form.Item>

					{accountType === 'bank' && (
						<>
							<Form.Item
								name="bank_name"
								label="Bank Name"
								rules={[{ required: true, message: 'Please enter bank name' }]}
							>
								<Input placeholder="Enter bank name" />
							</Form.Item>

							<Form.Item
								name="branch_name"
								label="Branch Name"
								rules={[{ required: true, message: 'Please enter branch name' }]}
							>
								<Input placeholder="Enter branch name" />
							</Form.Item>
						</>
					)}

					<Divider />

					<div className="flex justify-end">
						<Space>
							<Button
								onClick={() => {
									setIsModalOpen(false);
									accountForm.resetFields();
								}}
							>
								Cancel
							</Button>
							<Button
								type="primary"
								htmlType="submit"
								loading={isAddingAccount}
							>
								Add Account
							</Button>
						</Space>
					</div>
				</Form>
			</Modal>
		</AppLayout>
	);
};

export default PaymentAccounts;
